/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import { useForm } from 'react-hook-form';
import {
  InviteeRequest,
  RequestError,
  useAddInvitee,
  useGetEvent,
  useGetInviteeContributors,
} from 'api';
import {
  Alert,
  ButtonV2,
  Chip,
  ChipProps,
  Dialog,
  TextV2,
} from '@insights-ltd/design-library/components';
import { dateInPastForZone } from '@insights-ltd/design-library/utils';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';
import Notification from 'components/Notification';
import BreadcrumbLayout from 'components/layout/BreadcrumbLayout';
import { VALID_EMAIL_REGEX } from 'variables';
import { Box, useTheme } from '@mui/material';
import { combineQueryResults } from 'utils/combineQueryStatus';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { InviteeListOptions } from 'types/types';
import { LearnerContributors } from 'api/organisationGroups/organisationGroupsTypes';
import { readyToPurchase } from 'domain/invitee';
import { isDFCExperience } from 'domain/event';
import {
  awaitingResponseFilter,
  readyToPurchaseFilter,
  createdStatusFilter,
  profileCreatedFilter,
} from 'utils/eventFilters';
import { AddLearnersToTeamModal } from 'pages/EventLearners/EventLearners.helper';
import { exploreAccessRedirect } from 'utils/exploreAccessRedirect';
import LearnersAndContributorsSummary from './LearnersAndContributorsSummary';

type LearnersAndContributorsPillGroupType = {
  profileGenerated?: string;
  learnerContributors: LearnerContributors[];
  chipColor: ChipProps['color'];
  chipText: string;
};

const AddLearner = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const localTheme = useTheme();
  const [
    discoveryProfileRequiredDialogOpen,
    setDiscoveryProfileRequiredDialogOpen,
  ] = useState(false);

  const { mutate, isPending } = useAddInvitee();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset: resetForm,
    setError,
  } = useForm<InviteeRequest>();

  const onSubmit = (formData: InviteeRequest) =>
    mutate(
      { eventId, formData },
      {
        onSuccess: () => resetForm(),
        onError: (e) => {
          if (e instanceof RequestError) {
            if (e.errorCodes.find((code) => code === 'NO_PROFILE_AVAILABLE')) {
              setDiscoveryProfileRequiredDialogOpen(true);
            }
            if (
              e.errorCodes.find(
                (code) => code === 'EMAIL_ADDRESS_ALREADY_TAKEN',
              )
            ) {
              setError('email', {
                message: t(
                  'ui.event-management.events.add-learners-and-contributors.add-learner.error.email-address-taken',
                ),
              });
            }
          }
        },
      },
    );

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <TextV2
          variant="h3"
          sx={{
            marginTop: localTheme.spacing(spacingSizeMap.M),
            marginBottom: localTheme.spacing(spacingSizeMap.XS),
          }}
        >
          {t(
            'ui.event-management.events.add-learners-and-contributors.add-learner',
          )}
        </TextV2>
      </Grid>
      <Grid size={12}>
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                id="fullName"
                label={t(
                  'ui.event-management.events.add-learners.label.full-name',
                )}
                required
                variant="outlined"
                fullWidth
                type="text"
                error={Boolean(errors.fullName)}
                helperText={
                  errors.fullName &&
                  t('ui.event-management.events.create.error.name-required')
                }
                {...register('fullName', { required: true, min: 1 })}
                slotProps={{
                  htmlInput: {
                    'aria-label': t(
                      'ui.event-management.events.add-learners.label.full-name',
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                id="email"
                label={t(
                  'ui.event-management.events.add-learners.label.email-address',
                )}
                required
                variant="outlined"
                fullWidth
                type="email"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register('email', {
                  required: t(
                    'ui.event-management.events.create.error.value-email-required',
                  ),
                  pattern: VALID_EMAIL_REGEX,
                })}
                slotProps={{
                  htmlInput: {
                    'aria-label': t(
                      'ui.event-management.events.add-learners.label.email-address',
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending}
                sx={(theme) => ({
                  marginTop: theme.spacing(spacingSizeMap.XS),
                })}
                fullWidth
              >
                {t(
                  'ui.event-management.events.add-learners.button.add-learner',
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
        <Dialog
          open={discoveryProfileRequiredDialogOpen}
          title={t(
            'ui.event-management.events.add-learners-and-contributors.add-learner.error-modal.title',
          )}
          content={t(
            'ui.event-management.events.add-learners-and-contributors.add-learner.error-modal.content',
          )}
          secondaryButtonText={t(
            'ui.event-management.events.add-learners-and-contributors.add-learner.error-modal.dismiss',
          )}
          onClose={() => setDiscoveryProfileRequiredDialogOpen(false)}
        />
      </Grid>
    </Grid>
  );
};

const useLearnersAndContributorsData = (eventId: string) =>
  combineQueryResults([
    useGetEvent(eventId),
    useGetInviteeContributors(eventId),
  ]);

function otherLearnerIds(
  learnerContributors: LearnerContributors[],
  learnerId: string,
) {
  return learnerContributors
    .filter(({ learner }) => learner.id !== learnerId)
    .filter(({ learner }) => learner.profile?.status !== 'PURCHASED')
    .map((learnerContributor) => learnerContributor.learner.id);
}

const LearnersAndContributors = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const [isAddToTeamOpen, setIsAddToTeamOpen] = useState(false);
  const { data, status } = useLearnersAndContributorsData(eventId!);

  const event = data?.[0];
  const learnerContributors = data?.[1];

  const eventRedirect = exploreAccessRedirect(event);

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }

  if (status === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.events.overview.error-loading-event')}
      />
    );
  }

  if (eventRedirect) {
    return <Navigate to={eventRedirect} replace />;
  }

  const confirmedEvent = event!;
  const confirmedLearnerContributors = learnerContributors!;

  const isCancelled = confirmedEvent.eventStatus === 'CANCELLED';

  const invitees =
    learnerContributors?.map(
      (learnerContributor) => learnerContributor.learner,
    ) || [];

  const isNotExperienceEnded = !dateInPastForZone(
    confirmedEvent.extendedEndDate,
    confirmedEvent.timezone,
  );

  const crudOperationsEnabled: boolean = !isCancelled && isNotExperienceEnded;

  const filteredInvitees = (
    searchText === ''
      ? learnerContributors!
      : learnerContributors!.filter(
          (invitee) =>
            invitee.learner.email
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            invitee.learner.email
              .toLowerCase()
              .includes(searchText.toLowerCase()),
        )
  )
    .map((invitee) => {
      if (
        readyToPurchase(invitee.learner) &&
        (invitee.learner.profile?.status === 'REUSED' ||
          invitee.learner.profile?.status === 'PURCHASED')
      ) {
        return {
          ...invitee,
          profileStatus: 'PROFILE_GENERATED' as InviteeListOptions,
        };
      }
      return invitee;
    })
    .sort();

  type GroupedLearnerContributors = {
    awaitingResponse: LearnerContributors[];
    readyToPurchase: LearnerContributors[];
    created: LearnerContributors[];
  };

  const groupedFilteredContributors =
    confirmedLearnerContributors.reduce<GroupedLearnerContributors>(
      (acc, item) => {
        if (awaitingResponseFilter(item)) {
          return { ...acc, awaitingResponse: [...acc.awaitingResponse, item] };
        }

        if (readyToPurchaseFilter(item)) {
          return { ...acc, readyToPurchase: [...acc.readyToPurchase, item] };
        }

        if (createdStatusFilter(item)) {
          return { ...acc, created: [...acc.created, item] };
        }

        return acc;
      },
      {
        awaitingResponse: [],
        readyToPurchase: [],
        created: [],
      },
    );

  const learnerContributorsFilteredData: LearnersAndContributorsPillGroupType[] =
    [
      {
        learnerContributors: groupedFilteredContributors.awaitingResponse,
        chipColor: 'orange',
        chipText:
          'ui.event-management.events.add-learners-and-contributors.contributors-status.awaiting-responses',
      },
      {
        learnerContributors: groupedFilteredContributors.readyToPurchase,
        chipColor: 'pink',
        chipText: 'ui.event-management.events.invitee.status.ready-to-purchase',
      },
      {
        learnerContributors: groupedFilteredContributors.created,
        chipColor: 'darkGreen',
        chipText: 'ui.event-management.events.invitee.status.profile-created',
      },
      {
        learnerContributors: filteredInvitees.filter(profileCreatedFilter),
        chipColor: 'darkGreen',
        chipText: 'ui.event-management.events.invitee.status.profile-created',
      },
    ];

  if (!isDFCExperience(confirmedEvent.eventType)) {
    return <Navigate to={`/experiences/${eventId}`} />;
  }

  return (
    <BreadcrumbLayout
      fullWidth
      activeTextKey={
        crudOperationsEnabled
          ? 'ui.event-management.events.summary.manage-learners-and-contributors'
          : 'ui.event-management.events.summary.view-learners-and-contributors'
      }
      crumbs={{
        '/': t('ui.event-management.events.nav.home'),
        '/experiences': t('ui.event-management.events.title'),
        [`/experiences/${eventId}`]: confirmedEvent.name,
      }}
      headingKey={
        crudOperationsEnabled
          ? 'ui.event-management.events.add-learners-and-contributors.title'
          : 'ui.event-management.events.add-learners-and-contributors.read-only.title'
      }
      titleKey="ui.event-management.title.add-learners-and-contributors"
      titleKeyOptions={{
        eventName: confirmedEvent.name,
      }}
      parentLink={`/experiences/${eventId}`}
      secondaryContent={
        !isCancelled ? (
          <ButtonV2
            id="event-add-to-team-button"
            sx={{ paddingRight: 2 }}
            variant="outlined"
            buttonText={t('ui.event-management.add-to-team.button')}
            onClick={() => setIsAddToTeamOpen(true)}
          />
        ) : undefined
      }
    >
      <Grid container columns={12} spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <TextV2 color="textSecondary">
              {t(
                'ui.event-management.events.add-learners-and-contributors.sub-title',
              )}
            </TextV2>
          </Box>
        </Grid>
        <Grid size={12}>
          {!crudOperationsEnabled && (
            <Alert severity="warning">
              {t(
                'ui.event-management.events.add-learners-and-contributors.read-only.archived.sub-title',
              )}
            </Alert>
          )}
          {crudOperationsEnabled && (
            <Notification
              message={t(
                'ui.event-management.events.learners.notification.get-dfc-discovery-purchase',
              )}
            />
          )}
        </Grid>
        {crudOperationsEnabled && (
          <Grid size={12}>
            {confirmedEvent.eventType === 'INSIGHTS_DISCOVERY_FULL_CIRCLE' &&
            confirmedLearnerContributors.length < 13 ? (
              <AddLearner eventId={eventId!} />
            ) : (
              t(
                'ui.event-management.events.learners.notification.get-dfc-max-limit-reached',
              )
            )}
          </Grid>
        )}
        <Grid size={12}>
          <Divider
            sx={(theme) => ({
              marginBottom: theme.spacing(spacingSizeMap.S),
              marginTop: theme.spacing(spacingSizeMap.S),
            })}
          />
        </Grid>
        {confirmedLearnerContributors.length > 0 && (
          <Grid size={12}>
            <Grid container columns={12} spacing={2}>
              <Grid size={12}>
                <TextV2 variant="h3">
                  {t(
                    'ui.event-management.events.add-learners-and-contributors.summary.title',
                    { count: confirmedLearnerContributors.length },
                  )}
                </TextV2>
              </Grid>
              <Grid size={12}>
                <Grid
                  container
                  columns={12}
                  sx={(theme) => ({
                    padding: `${theme.spacing(
                      spacingSizeMap.XS,
                    )} ${theme.spacing(spacingSizeMap.S)}`,
                    color: theme.palette.grey[100],
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: theme.spacing(0.5),
                  })}
                >
                  <Grid size={4}>
                    <TextV2 variant="bodyBold">
                      {t(
                        'ui.event-management.events.add-learners-and-contributors.learner-header.full-name-email-address',
                      )}
                    </TextV2>
                  </Grid>
                  <Grid size={8}>
                    <TextV2 variant="bodyBold">
                      {t(
                        'ui.event-management.events.add-learners-and-contributors.learner-header.contributors',
                      )}
                    </TextV2>
                  </Grid>
                </Grid>
              </Grid>
              {learnerContributorsFilteredData.flatMap(
                (learnerContributorData) =>
                  learnerContributorData.learnerContributors.length > 0
                    ? [
                        <Grid key={learnerContributorData.chipText} size={12}>
                          <Chip
                            label={t(learnerContributorData.chipText)}
                            color={learnerContributorData.chipColor}
                          />
                        </Grid>,
                        ...learnerContributorData.learnerContributors.map(
                          ({ learner, contributors }) => (
                            <Grid key={learner.id} size={12}>
                              <LearnersAndContributorsSummary
                                event={confirmedEvent}
                                learner={learner}
                                contributors={contributors}
                                learnerIds={otherLearnerIds(
                                  confirmedLearnerContributors,
                                  learner.id,
                                )}
                                crudOperationsEnabled={crudOperationsEnabled}
                                allContributorsRemoved={
                                  learner.status === 'READY_TO_PURCHASE' &&
                                  contributors.length === 0
                                }
                              />
                            </Grid>
                          ),
                        ),
                      ]
                    : [],
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
      {confirmedEvent && (
        <AddLearnersToTeamModal
          invitees={invitees}
          event={confirmedEvent}
          isAddToTeamOpen={isAddToTeamOpen}
          setIsAddToTeamOpen={setIsAddToTeamOpen}
        />
      )}
    </BreadcrumbLayout>
  );
};

export default LearnersAndContributors;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
